import React, { FunctionComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// components
import { Hold } from './Hold';
import { PrivateOfferItemPrices } from './PrivateOfferItemPrices';
import { SaleItemPrices } from './SaleItemPrices';
import { NetItemPrices } from './NetItemPrices';
import { RetailItemPrices } from './RetailItemPrices';
import { textPriceValue } from './messages/priceMessages';
import SoldWordIcon from 'dibs-icons/exports/Sold';

import { PRICE_TYPES, TEXT_SIZE_SMALL, TEXT_SIZE_MEDIUM } from './constants';

// helpers
import { getPriceFromAmountListByCurrency, isValidTextPriceType } from './helpers';

// styles
import styles from './PriceDisplay.scss';

// types
import { PriceDisplay_item as ItemType } from './__generated__/PriceDisplay_item.graphql';
import { PriceDisplay_sku as SkuType } from './__generated__/PriceDisplay_sku.graphql';

type Props = {
    currency?: string;
    item: ItemType;
    sku?: SkuType;
    textSize?: string;
    useSoldIcon?: boolean;
};

export const PriceDisplayComponent: FunctionComponent<Props> = ({
    item,
    sku,
    currency = 'USD',
    textSize = TEXT_SIZE_MEDIUM,
    useSoldIcon = false,
}) => {
    const holdDisplayTime = item?.activeHold?.displayTimeToExpire || null;
    const itemPriceDisplay = sku ? sku?.displayPrice?.[0] : item?.displayPrice?.[0];

    if (!itemPriceDisplay) {
        return <div className={styles.wrapper} />;
    }

    const {
        type,
        convertedAmountList,
        originalConvertedAmountList,
        privateOfferConvertedAmountList,
        quantityDisplay,
        percentageOff,
        showPriceLabel,
        offerExpirationDate,
    } = itemPriceDisplay;

    const showSold = type === PRICE_TYPES.SOLD;
    const showHold = type === PRICE_TYPES.HOLD || type === PRICE_TYPES.HOLD_CUSTOM;

    const price = getPriceFromAmountListByCurrency(convertedAmountList, currency);
    const originalPrice = getPriceFromAmountListByCurrency(originalConvertedAmountList, currency);
    const percentageValue = percentageOff ? parseInt(percentageOff) : undefined;

    // Getting priceComponent
    let priceComponent = null;
    const sharedPriceProps = {
        price,
        originalPrice,
        currency,
        quantityDisplay,
        hasPrivateOffer: !!privateOfferConvertedAmountList,
    };

    if (type === PRICE_TYPES.SALE) {
        // Item is on sale
        priceComponent = <SaleItemPrices {...sharedPriceProps} percentageOff={percentageValue} />;
    } else if (type === PRICE_TYPES.NET) {
        // Item has a net price and is being viewed by a trade user
        priceComponent = <NetItemPrices {...sharedPriceProps} percentageOff={percentageValue} />;
    } else if (type === PRICE_TYPES.RETAIL) {
        // Item only has a retail price (or is being viewed by a non-trade user)
        priceComponent = (
            <RetailItemPrices {...sharedPriceProps} showPriceLabel={!!showPriceLabel} />
        );
    } else if (isValidTextPriceType(type)) {
        // Item is unavailable or has no visible price (e.g. sold, unavailable or pur)
        priceComponent = (
            <div
                className={classnames({
                    [styles.sold]: showSold,
                    [styles.unavailable]: type === PRICE_TYPES.UNAVAILABLE,
                    [styles.pur]: type === PRICE_TYPES.PUR,
                })}
                data-tn={`price-${type}-price`}
            >
                {showSold && useSoldIcon ? (
                    <SoldWordIcon className={styles.icon} />
                ) : (
                    textPriceValue(type)
                )}
            </div>
        );
    }

    if (!priceComponent && !showHold) {
        return null;
    }

    let privateOffer;
    if (privateOfferConvertedAmountList) {
        const privateOfferPrice = getPriceFromAmountListByCurrency(
            privateOfferConvertedAmountList,
            currency
        );

        privateOffer = (
            <PrivateOfferItemPrices
                {...sharedPriceProps}
                price={privateOfferPrice}
                expiration={offerExpirationDate}
                showExpirationDate={!holdDisplayTime}
            />
        );
    }

    const priceWrapperClasses = classnames(styles.wrapper, {
        [styles.smallText]: textSize === TEXT_SIZE_SMALL,
    });

    return (
        <div className={priceWrapperClasses} data-tn="price-wrapper">
            {/* 'On Hold For You' OR 'On Hold' */}
            {(type === PRICE_TYPES.HOLD || type === PRICE_TYPES.HOLD_CUSTOM) && (
                <Hold holdType={type} holdDisplayTime={holdDisplayTime} />
            )}
            {/* Price */}
            {priceComponent}
            {/* show Private Offer if exists */}
            {privateOffer}
        </div>
    );
};

export const PriceDisplay = createFragmentContainer(PriceDisplayComponent, {
    item: graphql`
        fragment PriceDisplay_item on Item
            @argumentDefinitions(
                hasUserId: { type: "Boolean", defaultValue: false }
                userId: { type: "String!", defaultValue: "" }
                isTrade: { type: "Boolean", defaultValue: false }
            ) {
            activeHold {
                displayTimeToExpire
            }
            displayPrice(isTrade: $isTrade, buyerId: $userId) {
                originalConvertedAmountList {
                    currency
                    amount
                }
                convertedAmountList {
                    currency
                    amount
                }
                privateOfferConvertedAmountList {
                    currency
                    amount
                }
                quantityDisplay
                percentageOff
                type
                showPriceLabel
                showPriceVariability
                offerExpirationDate
            }
        }
    `,
    sku: graphql`
        fragment PriceDisplay_sku on ItemSkuType
            @argumentDefinitions(
                hasSkuId: { type: "Boolean", defaultValue: false }
                hasUserId: { type: "Boolean", defaultValue: false }
                userId: { type: "String!", defaultValue: "" }
                isTrade: { type: "Boolean", defaultValue: false }
            ) {
            ... @include(if: $hasSkuId) {
                serviceId
                displayPrice(isTrade: $isTrade, buyerId: $userId) {
                    originalConvertedAmountList {
                        currency
                        amount
                    }
                    convertedAmountList {
                        currency
                        amount
                    }
                    privateOfferConvertedAmountList {
                        currency
                        amount
                    }
                    quantityDisplay
                    percentageOff
                    type
                    showPriceLabel
                    showPriceVariability
                    offerExpirationDate
                }
            }
        }
    `,
});
